import * as React from 'react'
import { Button, Checkbox, ColorPicker, IconButton, Input, Label, Select } from 'uxp/components'
import ActionEditorLink, { extractAction, IAction } from '../action-editor/ActionEditor'
import { applyAtPath, IBaseProperty, IUIPropertyDefinition } from '../common'
import { BackgroundPositionEditor, CustomColorPicker, escapeUnit, Property, PropertyContainer, PropertyContainerTitle, PropertyGroup, PropertyGroupTitle, PropertyTitle, TextAlignEditor } from './common'
import { IBackgroundProps, ITextProps } from './properties'
import jp from 'jsonpath'
import { hasProperty, hasValue, parseValue } from '../../../util'
import { ImagePicker } from 'lucy/common-components'
import { IContextData } from '../../../hooks/useContextData'

export interface IContainerPropertyProps extends IBaseProperty {
    background?: IBackgroundProps
    text?: ITextProps
    onClick?: IAction
    removeShadow?: boolean
}

export function extractContainerProperties(props: IContainerPropertyProps, contextData?: IContextData): { styles: React.CSSProperties, onClick: IAction } {
    if (!props) return { styles: {}, onClick: extractAction(null) }
    let { background, text, removeShadow } = props

    let styles: React.CSSProperties = {}

    if (background) {
        if (background.image) {
            let { image, position, size } = background.image

            if (hasValue(image)) styles.backgroundImage = `url('${parseValue(image, contextData)}')`
            if (hasValue(position)) styles.backgroundPosition = position
            if (hasValue(size)) styles.backgroundSize = size
        }

        if (background.color && hasValue(background.color)) styles.backgroundColor = parseValue(background.color, contextData)
    }

    if (text) {
        let { color, fontSize, align } = text
        if (hasValue(color)) styles.color = parseValue(color, contextData)
        if (hasValue(fontSize)) styles.fontSize = fontSize
        if (hasValue(align)) styles.textAlign = align as any
    }
    let onClick = extractAction(props.onClick)

    return { styles, onClick }
}

export function ContainerProperty(defaultProps: IContainerPropertyProps, title?: string): IUIPropertyDefinition<IContainerPropertyProps> {
    return {
        defaults: defaultProps,
        render: (props, uxpContext, contextData, containerId) => {

            let _props = props?.uiProps || {}
            containerId = hasValue(containerId) ? containerId + '-' : ''

            return <PropertyContainer>
                <PropertyContainerTitle>{title || 'Container'}</PropertyContainerTitle>

                {hasProperty(_props, '$.background') &&
                    <PropertyGroup>
                        <PropertyGroupTitle>Background</PropertyGroupTitle>
                        {
                            hasProperty(_props, '$.background.color') && <>
                                <Property id={containerId + 'background-color'} >
                                    <PropertyTitle>Background Color</PropertyTitle>
                                    <CustomColorPicker contextData={props.contextData}
                                        color={_props.background.color || ''}
                                        onChange={s => {
                                            let newProps = applyAtPath(_props, '$.background.color', () => (s))
                                            props.onChange(newProps)
                                        }}
                                    />
                                </Property>
                            </>
                        }
                        {
                            hasProperty(defaultProps, '$.removeShadow') && <>
                                <Property id={containerId + ' remove-shadow'} >
                                    <PropertyTitle>Border Shadow</PropertyTitle>
                                    <Checkbox checked={!!_props.removeShadow}
                                        label={'Remove Shadow'}
                                        onChange={(b) => {
                                            let newProps = applyAtPath(_props, '$.removeShadow', () => b);
                                            props.onChange(newProps);
                                        }}
                                    />
                                </Property>
                            </>
                        }

                        {
                            hasProperty(defaultProps, '$.background.image') && <>
                                <Property id={containerId + 'background-image'} >
                                    <PropertyTitle>Background Image</PropertyTitle>
                                    <ImagePicker
                                        uxpContext={uxpContext}
                                        imageUrl={_props.background?.image?.image || ''}
                                        onChange={s => {
                                            let newProps = applyAtPath(_props, '$.background.image.image', () => (s))
                                            props.onChange({ ...newProps })
                                        }}
                                    />
                                </Property>

                                {
                                    (hasProperty(defaultProps, '$.background.image.position') &&
                                        !!(_props?.background?.image?.image)
                                    ) &&
                                    <Property id={containerId + 'background-image-position'} >
                                        <PropertyTitle>Background Position</PropertyTitle>
                                        <BackgroundPositionEditor
                                            position={_props.background?.image?.position || ''}
                                            onChange={s => {
                                                let newProps = applyAtPath(_props, '$.background.image.position', () => (s))
                                                console.log("___props", _props);

                                                props.onChange({ ...newProps })
                                            }}
                                        />
                                    </Property>
                                }
                                {
                                    (hasProperty(defaultProps, '$.background.image.size') &&
                                        !!(_props?.background?.image?.image)
                                    ) &&
                                    <Property id={containerId + 'background-image-size'} >
                                        <PropertyTitle>Background Size</PropertyTitle>
                                        <Select
                                            selected={_props.background?.image?.size || 'cover'}
                                            options={[
                                                { label: 'Original Size', value: 'auto' },
                                                { label: 'Fill Area', value: 'cover' },
                                                { label: 'Fit Into Area', value: 'contain' }
                                            ]}
                                            onChange={s => {
                                                let newProps = applyAtPath(_props, '$.background.image.size', () => (s))
                                                props.onChange(newProps)
                                            }}
                                            className="widget-designer-input"
                                        />
                                    </Property>
                                }

                            </>
                        }



                    </PropertyGroup>
                }


                {hasProperty(_props, '$.text') &&
                    <PropertyGroup>
                        <PropertyGroupTitle>Text</PropertyGroupTitle>

                        {
                            hasProperty(_props, '$.text.color') && <>
                                <Property id={containerId + 'text-color'} >
                                    <PropertyTitle>Color</PropertyTitle>
                                    <CustomColorPicker contextData={props.contextData}
                                        color={_props.text.color || ''}
                                        onChange={s => {
                                            let newProps = applyAtPath(_props, '$.text.color', () => (s))
                                            props.onChange(newProps)
                                        }}
                                    />
                                </Property>

                            </>
                        }

                        {
                            hasProperty(_props, '$.text.align') && <>
                                <Property id={containerId + 'text-align'} >
                                    <PropertyTitle>Align</PropertyTitle>
                                    <TextAlignEditor
                                        value={_props.text.align || ''}
                                        onChange={s => {
                                            let newProps = applyAtPath(_props, '$.text.align', () => (s))
                                            props.onChange(newProps)
                                        }}
                                    />
                                </Property>

                            </>
                        }

                        {
                            hasProperty(_props, '$.text.fontSize') && <>
                                <Property id={containerId + 'text-fontSize'} >
                                    <PropertyTitle>Font Size (px) </PropertyTitle>
                                    <Input
                                        type='number'
                                        value={escapeUnit(_props.text.fontSize || '', 'px')}
                                        onChange={s => {
                                            let num = Number(s) || 0
                                            if (num < 8) s = '8'
                                            else if (num > 200) s = '200'

                                            let newProps = applyAtPath(_props, '$.text.fontSize', () => (s + 'px'))
                                            props.onChange(newProps)
                                        }}

                                        inputAttr={{
                                            max: '200',
                                            min: '8'
                                        }}
                                    />
                                </Property>

                            </>
                        }
                    </PropertyGroup>
                }

                {hasProperty(_props, '$.onClick') &&
                    <PropertyGroup>
                        <Property id={containerId + 'onClick'} >
                            <PropertyTitle>Edit action</PropertyTitle>
                            <ActionEditorLink
                                uiProps={props.parentUIProps}
                                designer={props.designer}
                                action={_props.onClick}
                                onChange={s => {
                                    let newProps = applyAtPath(_props, '$.onClick', () => (s))
                                    props.onChange(newProps)
                                }}
                                contextData={contextData}
                                uxpContext={uxpContext}
                            />
                        </Property>
                    </PropertyGroup>
                }

            </PropertyContainer>
        }
    }

}